import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCog, ShieldOff, ShieldAlert, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";

const ESTADO_BADGE = {
    activo: "text-emerald-700 bg-emerald-50 border-emerald-200",
    suspendido: "text-amber-700 bg-amber-50 border-amber-200",
    revocado: "text-red-700 bg-red-50 border-red-200",
};

export default function Delegados() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtro, setFiltro] = useState("__all");
    const [openAction, setOpenAction] = useState(null); // {tipo, delegado}
    const [motivo, setMotivo] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const params = filtro !== "__all" ? { estado: filtro } : {};
            const { data } = await api.get("/gobierno/delegados", { params });
            setRows(data);
        } catch (err) {
            toast.error("No se pudo cargar el libro de delegados", {
                description: err?.response?.data?.detail,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtro]);

    const ejecutar = async () => {
        if (motivo.trim().length < 5) {
            toast.error("Motivo debe tener al menos 5 caracteres");
            return;
        }
        try {
            await api.post(
                `/gobierno/delegados/${openAction.delegado.id}/${openAction.tipo}`,
                { motivo },
            );
            toast.success(`Delegado ${openAction.tipo === "revocar" ? "revocado" : "suspendido"}`);
            setOpenAction(null);
            setMotivo("");
            load();
        } catch (err) {
            toast.error("No se pudo ejecutar la acción", {
                description: err?.response?.data?.detail,
            });
        }
    };

    return (
        <div className="p-8 lg:p-10 space-y-6 max-w-[1600px]" data-testid="delegados-page">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Gobierno · Fase 14
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <UserCog className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Libro de Delegados
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Registro autoritativo de delegados fiduciarios con instrumento notarial,
                        facultades y tope de monto por operación.
                    </p>
                </div>
                <div className="flex items-end gap-3">
                    <div>
                        <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                            Filtrar
                        </label>
                        <Select value={filtro} onValueChange={setFiltro}>
                            <SelectTrigger className="mt-1 w-[180px]" data-testid="filtro-estado">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all">Todos</SelectItem>
                                <SelectItem value="activo">Activos</SelectItem>
                                <SelectItem value="suspendido">Suspendidos</SelectItem>
                                <SelectItem value="revocado">Revocados</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={load}
                        data-testid="refresh-delegados"
                    >
                        <RefreshCw className="h-3.5 w-3.5 mr-2" /> Refrescar
                    </Button>
                </div>
            </div>

            <div className="rounded-md border border-border overflow-hidden bg-card">
                <div className="overflow-x-auto">
                    <table className="trf-table" data-testid="delegados-table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Puesto</th>
                                <th>RFC · CURP</th>
                                <th>Instrumento notarial</th>
                                <th>Facultades</th>
                                <th className="text-right">Tope MXN</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((d) => (
                                <tr key={d.id} data-testid={`delegado-row-${d.id}`}>
                                    <td className="font-medium">{d.nombre_completo}</td>
                                    <td className="text-sm">{d.puesto}</td>
                                    <td className="tabular text-[11px]">
                                        <div>{d.rfc}</div>
                                        <div className="text-muted-foreground">{d.curp}</div>
                                    </td>
                                    <td className="text-xs">
                                        <div className="font-medium">
                                            Esc. {d.instrumento_notarial?.numero_escritura}
                                        </div>
                                        <div className="text-muted-foreground">
                                            {d.instrumento_notarial?.notario} · Not. {d.instrumento_notarial?.notaria_numero} · {d.instrumento_notarial?.plaza}
                                        </div>
                                    </td>
                                    <td className="text-xs">
                                        {(d.facultades || []).map((f) => (
                                            <span
                                                key={f}
                                                className="badge-gold mr-1 mb-1 inline-block"
                                            >
                                                {f.replace(/_/g, " ")}
                                            </span>
                                        ))}
                                    </td>
                                    <td className="text-right tabular">
                                        ${Number(d.monto_maximo_mxn || 0).toLocaleString("es-MX")}
                                    </td>
                                    <td>
                                        <span
                                            className={`text-[11px] uppercase tracking-widest border rounded-md px-2 py-1 ${ESTADO_BADGE[d.estado] || ""}`}
                                        >
                                            {d.estado}
                                        </span>
                                    </td>
                                    <td className="whitespace-nowrap">
                                        {d.estado === "activo" && (
                                            <>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() =>
                                                        setOpenAction({ tipo: "suspender", delegado: d })
                                                    }
                                                    data-testid={`suspender-${d.id}`}
                                                    className="mr-1"
                                                >
                                                    <ShieldAlert className="h-3 w-3 mr-1" />
                                                    Suspender
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() =>
                                                        setOpenAction({ tipo: "revocar", delegado: d })
                                                    }
                                                    data-testid={`revocar-${d.id}`}
                                                >
                                                    <ShieldOff className="h-3 w-3 mr-1" />
                                                    Revocar
                                                </Button>
                                            </>
                                        )}
                                        {d.estado === "suspendido" && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() =>
                                                    setOpenAction({ tipo: "revocar", delegado: d })
                                                }
                                            >
                                                <ShieldOff className="h-3 w-3 mr-1" />
                                                Revocar definitivo
                                            </Button>
                                        )}
                                        {d.estado === "revocado" && (
                                            <span className="text-xs text-muted-foreground">
                                                {d.fecha_revocacion?.slice(0, 10)}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={8} className="text-center py-10 text-muted-foreground">
                                        Sin delegados registrados.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={!!openAction} onOpenChange={(o) => !o && setOpenAction(null)}>
                <DialogContent data-testid="accion-delegado-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {openAction?.tipo === "revocar" ? "Revocar" : "Suspender"} delegado
                        </DialogTitle>
                    </DialogHeader>
                    {openAction && (
                        <div className="space-y-3">
                            <div className="text-sm text-muted-foreground">
                                <div>
                                    <strong>{openAction.delegado.nombre_completo}</strong> · {openAction.delegado.puesto}
                                </div>
                                {openAction.tipo === "revocar" && (
                                    <div className="text-red-600 mt-2 text-xs">
                                        La revocación es permanente. El delegado no podrá firmar
                                        instrucciones nuevas.
                                    </div>
                                )}
                            </div>
                            <Textarea
                                value={motivo}
                                onChange={(e) => setMotivo(e.target.value)}
                                placeholder="Motivo (mínimo 5 caracteres)"
                                data-testid="motivo-accion"
                                rows={3}
                            />
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpenAction(null)}>
                            Cancelar
                        </Button>
                        <Button onClick={ejecutar} data-testid="confirmar-accion">
                            Confirmar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
